import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Activity, Calendar, Clock, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import Layout from '../components/Layout';
import { Tour } from '../types/tours';
import TourDetailGallery from '../components/tours/TourDetailGallery';
import TourDetailHero from '../components/tours/TourDetailHero';
import TourDetailInfo from '../components/tours/TourDetailInfo';
import TourDetailBooking from '../components/tours/TourDetailBooking';
import TourDetailTabs from '../components/tours/TourDetailTabs';
import RelatedTours from '../components/tours/RelatedTours';
import TourDetailSkeleton from '../components/tours/TourDetailSkeleton';

const TourDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [tour, setTour] = useState<Tour | null>(null);
  const [availableTours, setAvailableTours] = useState<Tour[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchTour = async () => {
      try {
        setLoading(true);
        // Fetch tour data
        const { data: tourData, error: tourError } = await supabase
          .from('tours')
          .select(`
            *,
            tour_images (
              id,
              image_url,
              image_type,
              display_order
            ),
            tour_shared_items (
              id,
              item_id,
              display_order,
              shared_items (*)
            )
          `)
          .eq('slug', slug)
          .eq('status', 'published')
          .maybeSingle();

        if (tourError) {
          console.error('Error fetching tour:', tourError);
          throw tourError;
        }
        
        if (!tourData) {
          // Tour not found, fetch available tours
          const { data: allTours, error: allToursError } = await supabase
            .from('tours')
            .select(`
              *,
              tour_images (
                id,
                image_url,
                image_type,
                display_order
              )
            `)
            .eq('status', 'published')
            .order('display_order', { ascending: true });

          if (allToursError) {
            console.error('Error fetching available tours:', allToursError);
          } else {
            setAvailableTours(allTours || []);
          }
          
          setError('Tour not found');
          setLoading(false);
          return;
        }

        setTour(tourData);
      } catch (err) {
        console.error('Error fetching tour:', err);
        setError('Tour not found');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchTour();
    }
  }, [slug, navigate]);
  if (loading) return <TourDetailSkeleton />;
  
  if (error || !tour) {
    return (
      <Layout>
        {/* Hero Section */}
        <div className="bg-gradient-to-b from-[#2A382A] to-white">
          <div className="pt-20 pb-16">
            <div className="container max-w-5xl mx-auto px-4 md:px-8 text-center">
              <h1 className="font-barrio text-4xl md:text-6xl text-white mb-4">
                Tour Not Found
              </h1>
              <p className="text-white/80 text-lg md:text-xl mb-8">
                The tour you're looking for doesn't exist, but we have other amazing adventures waiting for you!
              </p>
              <Link 
                to="/" 
                className="inline-flex items-center px-6 py-3 bg-white text-[#2A382A] font-semibold rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ChevronLeft className="w-5 h-5 mr-2" />
                Back to Home
              </Link>
            </div>
          </div>
        </div>

        {/* Available Tours Section */}
        {availableTours.length > 0 && (
          <section className="bg-white py-16">
            <div className="container max-w-6xl mx-auto px-4 md:px-8">
              <h2 className="font-barrio text-3xl md:text-4xl text-[#2A382A] mb-8 text-center">
                Available Tours
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {availableTours.map((availableTour) => {
                  const carouselImages = availableTour.tour_images?.filter(img => img.image_type === 'carousel') || [];
                  const heroImage = carouselImages.sort((a, b) => a.display_order - b.display_order)[0]?.image_url;
                  
                  return (
                    <div key={availableTour.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                      {heroImage && (
                        <div className="h-48 bg-gray-200 overflow-hidden">
                          <img 
                            src={heroImage} 
                            alt={availableTour.name}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      )}
                      <div className="p-6">
                        <h3 className="font-barrio text-xl text-[#2A382A] mb-2">
                          {availableTour.name}
                        </h3>
                        <div className="flex items-center text-gray-600 mb-2">
                          <MapPin className="w-4 h-4 mr-1" />
                          <span className="text-sm">{availableTour.location}</span>
                        </div>
                        <div className="flex items-center text-gray-600 mb-2">
                          <Clock className="w-4 h-4 mr-1" />
                          <span className="text-sm">{availableTour.departure} • {availableTour.duration}</span>
                        </div>
                        <p className="text-gray-700 text-sm mb-4 line-clamp-3">
                          {availableTour.tour_card_description}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="text-[#2A382A] font-semibold">
                            From ${availableTour.group_tour_price}
                          </div>
                          <div className="flex gap-2">
                            <Link
                              to={`/tours/${availableTour.slug}`}
                              className="px-4 py-2 bg-[#2A382A] text-white text-sm rounded-lg hover:bg-[#1f2a1f] transition-colors"
                            >
                              View Details
                            </Link>
                            <Link
                              to={`/booking?tour=${availableTour.slug}`}
                              className="px-4 py-2 border border-[#2A382A] text-[#2A382A] text-sm rounded-lg hover:bg-[#2A382A] hover:text-white transition-colors"
                            >
                              Book Now
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#2A382A] via-[#2A382A] via-[#2A382A] via-[#2A382A] via-[#2A382A] to-white">
        <div className="pt-20">
          <TourDetailHero tour={tour} />
        </div>
      </div>

      {/* Info Section */}
      <section className="bg-white">
        <div className="container max-w-5xl mx-auto px-4 py-16 md:px-8">
          <TourDetailInfo tour={tour} />
        </div>
      </section>

      {/* Booking Section */}
      <section className="bg-[#F2F3F4]">
        <div className="container max-w-5xl mx-auto px-4 py-16 md:px-8">
          <TourDetailBooking tour={tour} />
        </div>
      </section>

      {/* Gallery Section */}
      <section className="bg-gradient-to-b from-[#F2F3F4] to-white">
        <div className="container max-w-5xl mx-auto px-4 py-16 md:px-8">
          <h2 className="font-barrio text-4xl md:text-5xl text-[#2A382A] mb-8">Gallery</h2>
          <TourDetailGallery tour={tour} />
        </div>
      </section>

      {/* What's Included & What to Bring Section */}
      <section className="bg-gradient-to-b from-white to-[#F2F3F4]">
        <div className="container max-w-5xl mx-auto px-4 py-16 md:px-8">
          <TourDetailTabs tour={tour} />
        </div>
      </section>

      {/* Related Tours Section */}
      <section className="bg-gradient-to-b from-[#F2F3F4] to-white">
        <div className="container max-w-5xl mx-auto px-4 py-16 md:px-8">
          <div className="flex justify-between items-center mb-8">
            <h2 className="font-barrio text-4xl md:text-5xl text-[#2A382A]">
              You might also like...
            </h2>
          </div>
          <RelatedTours currentTourId={tour.id} />
        </div>
      </section>
    </Layout>
  );
};

export default TourDetail;